import { useEffect, useState } from "react";
import { useAppDispatch } from "@grassrootly/rn-core";
import { GetSeasonStatistics, IPlayerSeasonStats } from "@grassrootly/api";
import { competitionsActions } from "../../api/competitions/CompetitionsReducer";
import { useSeasonStats } from "../../api/competitions/CompetitionsSelector";
import moment from "moment";

export const useCompetitionStats = (competitionId: number, seasonId: number) => {
  const dispatch = useAppDispatch();
  const cachedStats = useSeasonStats(seasonId);
  const [stats, setStats] = useState<IPlayerSeasonStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"monthly" | "allseason">("allseason");
  const [month, setMonth] = useState(moment().format("MMMM YYYY"));

  const fetchStats = async (dateFrom?: string | null, dateTo?: string | null) => {
    try {
      setLoading(true);
      const data = await GetSeasonStatistics({ 
        seasonId,
        dateFrom,
        dateTo
      });
      
      // Only cache all-season stats
      if (!dateFrom && !dateTo) {
        dispatch(competitionsActions.setSeasonStats({ seasonId, stats: data }));
      }
      
      setStats(data);
    } catch (error) {
      console.error("Error fetching season stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats when seasonId or mode changes
  useEffect(() => {
    if (mode === "allseason") {
      // Use cached stats if available, otherwise fetch
      if (cachedStats.length > 0) {
        setStats(cachedStats);
      } else {
        fetchStats();
      }
    } else if (mode === "monthly") {
      // Fetch monthly stats
      const startOfMonth = moment(month, "MMMM YYYY").startOf("month").toISOString();
      const endOfMonth = moment(month, "MMMM YYYY").endOf("month").toISOString();
      fetchStats(startOfMonth, endOfMonth);
    }
  }, [seasonId, mode, month]);

  const navigateMonth = (direction: number) => {
    const newMonth = moment(month, "MMMM YYYY").add(direction, "months");
    const currentMonth = moment().startOf("month");
    
    // Don't allow navigating to future months
    if (direction > 0 && newMonth.isAfter(currentMonth)) {
      return;
    }
    
    setMonth(newMonth.format("MMMM YYYY"));
  };

  const refreshStats = async () => {
    if (mode === "allseason") {
      await fetchStats();
    } else if (mode === "monthly") {
      const startOfMonth = moment(month, "MMMM YYYY").startOf("month").toISOString();
      const endOfMonth = moment(month, "MMMM YYYY").endOf("month").toISOString();
      await fetchStats(startOfMonth, endOfMonth);
    }
  };

  const isCurrentOrFutureMonth = moment(month, "MMMM YYYY").startOf("month").isSameOrAfter(moment().startOf("month"));

  return {
    stats,
    loading,
    refreshStats,
    mode,
    setMode,
    month,
    navigateMonth,
    isCurrentOrFutureMonth,
  };
};
